import React, { useContext, useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  Link
} from '@material-ui/core';
import { I18nContext } from 'translations';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { pqrStatusClass } from 'utils';
import { PQR_STATUSES } from 'utils/constants';
import { format } from 'date-fns';
import EmptyData from '../EmptyData';
import styles from '../style';


const getStatusBgColor = (status_cd) => {
  if (status_cd === 4) return '#effaee';
  if (status_cd === 1 || status_cd === 2) return '#fff6e4';
  return 'rgb(236,233,256)';
};

const getStatusColor = (status_cd) => {
  if (status_cd === 4) return '#274c44';
  if (status_cd === 1 || status_cd === 2) return '#663d00';
  return 'rgb(93,48,130)';
};

const ClaimsTable = ({
  data,
  classes,
  openClaim,
  getClaims,
  recordsPerPage
}) => {
  const [currentPage, setCurrentPage] = useState(data.page);
  const { translate } = useContext(I18nContext);

  useEffect(() => {
    setCurrentPage(data.page);
  }, [data]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    getClaims(newPage);
  };

  if (!data || (data?.data.length <= 0)) {
    return <EmptyData message={translate('thereIsNoData')} />;
  }

  return (
    <>
      <TableContainer>
        <Table className={classes.tableStyle} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{translate('claims.table.state')}</TableCell>
              <TableCell>{translate('claims.table.date')}</TableCell>
              <TableCell>{translate('claims.table.id')}</TableCell>
              <TableCell>{translate('claims.table.filter.bookingId')}</TableCell>
              <TableCell>{translate('claims.table.subject')}</TableCell>
              <TableCell>{translate('claims.form.typeOfPqr')}</TableCell>
              <TableCell>{translate('claims.table.claimant')}</TableCell>
              <TableCell>{translate('claims.table.reopenDate')}</TableCell>
              <TableCell>{translate('claims.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.data || []).map(claim => (
              <TableRow
                hover
                key={claim?.id}
              >
                <TableCell>
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    <span className={pqrStatusClass(claim?.status_cd)}>
                      {translate(PQR_STATUSES[claim?.status_cd])}
                    </span>
                    {(claim?.has_unread_messages || (claim?.status_cd === 4 && claim?.rating == null)) && (
                      <span style={{ position: 'absolute', top: -10, left: -4, display: 'flex', gap: 2 }}>
                        {claim?.has_unread_messages && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 3, background: '#fff', border: `2px solid ${getStatusBgColor(claim?.status_cd)}` }}>
                            <MailOutlineIcon style={{ fontSize: 10, color: getStatusColor(claim?.status_cd) }} />
                          </span>
                        )}
                        {claim?.status_cd === 4 && claim?.rating == null && (
                          <span title={translate('claims.rating.pending')} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 3, background: '#fffbea', border: '2px solid #fef3c7' }}>
                            <StarBorderIcon style={{ fontSize: 10, color: '#b45309' }} />
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  {format(new Date(claim?.created_at), 'dd/MM/yyyy, hh:mm a')}
                </TableCell>
                <TableCell>
                  <Button
                    color="primary"
                    onClick={() => openClaim(claim?.id)}
                    style={{ padding: 0, minWidth: 'unset' }}
                  >
                    {claim?.ticket_number || claim?.id}
                  </Button>
                </TableCell>
                <TableCell>
                  {claim?.booking_id
                    ? <Link href={`/bookings/${claim.booking_id}`} target="_blank" rel="noopener noreferrer">{claim.booking_id}</Link>
                    : '-'}
                </TableCell>
                <TableCell>
                  {typeof claim?.type_of_request === 'string' ? claim?.type_of_request : (claim?.type_of_request_cd || '-')}
                </TableCell>
                <TableCell>
                  {claim?.subject}
                </TableCell>
                <TableCell>
                  {claim?.passenger_name || '-'}
                </TableCell>
                <TableCell>
                  {claim?.reopened_at ? format(new Date(claim.reopened_at), 'dd/MM/yyyy, hh:mm a') : '-'}
                </TableCell>
                <TableCell>
                  {claim?.status_cd === 4 ? (
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      onClick={() => openClaim(claim?.id)}
                      style={{ borderRadius: '17px' }}
                    >
                      {translate('claims.table.actions.reOpen')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outlined"
                        color="default"
                        onClick={() => openClaim(claim?.id)}
                        style={{ borderRadius: '17px' }}
                      >
                        {translate('claims.table.actions.show')}
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.total ? data.total : data.pages * recordsPerPage}
        page={currentPage - 1}
        rowsPerPageOptions={[]}
        rowsPerPage={recordsPerPage}
        onChangePage={(event, newPage) => handleChangePage(event, newPage + 1)}
      />
    </>
  );
};

function areEqual(prevProps, nextProps) {
  return prevProps.data === nextProps.data;
}

export default withStyles(styles)(React.memo(ClaimsTable, areEqual));
